import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';
import styles from 'Components/PdfFormTemplates/pdfFormTemplates.module.css';
import { userSelector } from 'Containers/User/selector';
import { Api } from 'Utils/api';

interface Props {
  isOpen: boolean;
  templateId: number;
  companyId: number;
  onClose: () => void;
}

function TestFormModal({ isOpen, templateId, companyId, onClose }: Props) {
  const user = useSelector(userSelector, areEqual);

  const [projects, setProjects] = useState<any[]>([]);
  const [projectId, setProjectId] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const getProjectLabel = useCallback(
    (p: any) => p.address?.address || p.uid || `Project #${p.id}`,
    []
  );

  // Fetch projects for the template's company (any project in that company).
  // Scoped server-side to companyId, so no cross-company leakage.
  const fetchProjects = useCallback(
    async (searchValue = '') => {
      if (!companyId) return;
      const params: Record<string, any> = {
        // Only `address` is needed for the label; the company-projects endpoint
        // does NOT allow the count includes (units_count/floors_count/
        // photos_count) and 400s the whole request if they're sent.
        include: 'address',
        sort: '-created_at',
        page: 1,
      };
      // Backend requires filter[search] to be >= 2 chars; omit it otherwise.
      const trimmed = searchValue.trim();
      if (trimmed.length >= 2) params['filter[search]'] = trimmed;

      try {
        const { data } = await Api.get(`companies/${companyId}/projects`, { params });
        setProjects(data?.data || []);
      } catch (err) {
        setProjects([]);
      }
    },
    [companyId]
  );

  // Load initial projects when modal opens
  useEffect(() => {
    if (isOpen && companyId) {
      fetchProjects();
    }
  }, [isOpen, companyId, fetchProjects]);

  // Debounced server-side search when typing
  const onSearchChange = useCallback(
    (value: string) => {
      setProjectSearch(value);
      setProjectId('');
      setProjectDropdownOpen(true);

      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        fetchProjects(value);
      }, 300);
    },
    [fetchProjects]
  );

  // Clean up timer on unmount
  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  const onProjectSelect = useCallback(
    (project: any) => {
      setProjectId(String(project.id));
      setProjectSearch(getProjectLabel(project));
      setProjectDropdownOpen(false);
    },
    [getProjectLabel]
  );

  const onSubmit = useCallback(async () => {
    if (!templateId || !projectId) return;

    // Open a blank tab synchronously within the click handler to avoid popup blockers
    const newTab = window.open('about:blank', '_blank');

    setSubmitting(true);
    setError('');

    try {
      const { data } = await Api.post('/pdf-form-submissions', {
        pdf_form_template_id: templateId,
        project_id: Number(projectId),
        client_name: user?.full_name || 'Test User',
        client_email: user?.email || 'test@test.com',
      });

      if (data?.data?.uuid) {
        if (newTab) {
          newTab.location.href = `/pdf-form-sign/${data.data.uuid}`;
        } else {
          // Fallback if popup was still blocked
          window.open(`/pdf-form-sign/${data.data.uuid}`, '_blank');
        }
        handleClose();
      } else {
        if (newTab) newTab.close();
        setError('Submission created but no UUID returned.');
      }
    } catch (err: any) {
      if (newTab) newTab.close();
      setError(err?.response?.data?.message || 'Failed to create test submission.');
    } finally {
      setSubmitting(false);
    }
  }, [templateId, projectId, user]);

  const handleClose = useCallback(() => {
    setProjectId('');
    setProjectSearch('');
    setProjectDropdownOpen(false);
    setSubmitting(false);
    setError('');
    onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className={styles.modalOverlay} onClick={handleClose}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h2>Test Form</h2>
        <p style={{ fontSize: 14, color: '#6c757d', margin: '0 0 16px' }}>
          Select a project to create a test submission and open the signing page.
        </p>

        <div className={styles.formGroup}>
          <label>Project</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              value={projectSearch}
              onChange={(e) => onSearchChange(e.target.value)}
              onFocus={() => setProjectDropdownOpen(true)}
              onBlur={() => setProjectDropdownOpen(false)}
              placeholder="Search projects..."
            />
            {projectDropdownOpen && (
              <div className={styles.projectDropdown}>
                {projects.length > 0 ? (
                  projects.map((p: any) => (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div
                      key={p.id}
                      className={styles.projectDropdownItem}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onProjectSelect(p);
                      }}
                    >
                      {getProjectLabel(p)}
                    </div>
                  ))
                ) : (
                  <div className={styles.projectDropdownEmpty}>No projects found</div>
                )}
              </div>
            )}
          </div>
        </div>

        {error && (
          <p style={{ color: '#dc3545', fontSize: 13, margin: '0 0 12px' }}>{error}</p>
        )}

        <div className={styles.modalActions}>
          <button type="button" className={styles.btnSecondary} onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={onSubmit}
            disabled={!projectId || submitting}
          >
            {submitting ? 'Creating...' : 'Create & Open'}
          </button>
        </div>
      </div>
    </div>
  );
}

const TestFormModalMemo = memo(TestFormModal, areEqual);
export { TestFormModalMemo as TestFormModal };
